import React from 'react';
import {
  Box,
  CloseButton,
  Flex,
  Icon,
  useColorModeValue,
  Text,
  Heading,
  VStack,
} from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { FiHome, FiSettings, FiUsers, FiFileText, FiLayout, FiCheckSquare } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';

// ナビゲーション項目
const NavItems = [
  { name: 'ダッシュボード', icon: FiHome, path: '/dashboard' },
  { name: 'プロジェクト', icon: FiFileText, path: '/projects' },
  { name: 'OCR検証', icon: FiCheckSquare, path: '/verification' },
];

// 管理者専用ナビゲーション項目
const AdminNavItems = [
  { name: '管理者ダッシュボード', icon: FiLayout, path: '/admin/dashboard' },
  { name: 'ユーザー管理', icon: FiUsers, path: '/admin/users' },
  { name: 'システム設定', icon: FiSettings, path: '/admin/settings' },
];

/**
 * サイドバーコンポーネント
 */
const Sidebar = ({ onClose, ...rest }) => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'privileged';
  
  return (
    <Box
      transition="0.3s ease"
      bg={useColorModeValue('white', 'gray.900')}
      borderRight="1px"
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{ base: 'full', md: 60 }}
      pos="fixed"
      h="full"
      {...rest}
    >
      <Flex h="20" alignItems="center" mx="4" justifyContent={{ base: 'space-between', md: 'center' }}>
        {/* サイドバーロゴ */}
        <Logo width="160px" height="40px" />
        {/* モバイル用閉じるボタン */}
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} />
      </Flex>
      
      {/* ナビゲーションリンク */}
      <VStack spacing={2} align="stretch" px={4} py={4}>
        {/* 通常のナビゲーション項目 */}
        {NavItems.map((navItem) => (
          <NavItem
            key={navItem.name}
            icon={navItem.icon}
            path={navItem.path}
          >
            {navItem.name}
          </NavItem>
        ))}
        
        {/* 管理者専用セクション */}
        {isAdmin && (
          <>
            <Box pt={4} pb={2}>
              <Text
                px={3}
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="wider"
                color={useColorModeValue('gray.500', 'gray.400')}
                textTransform="uppercase"
              >
                管理者メニュー
              </Text>
            </Box>
            
            {/* 管理者専用ナビゲーション項目 */}
            {AdminNavItems.map((navItem) => (
              <NavItem
                key={navItem.name}
                icon={navItem.icon}
                path={navItem.path}
              >
                {navItem.name}
              </NavItem>
            ))}
          </>
        )}
      </VStack>
    </Box>
  );
};

/**
 * サイドバーのナビゲーション項目コンポーネント
 */
const NavItem = ({ icon, path, children, ...rest }) => {
  return (
    <NavLink
      to={path}
      style={({ isActive }) => ({
        backgroundColor: isActive ? useColorModeValue('brand.50', 'brand.900') : 'transparent',
        color: isActive ? useColorModeValue('brand.600', 'brand.300') : 'inherit',
        borderRadius: '6px',
        display: 'block',
      })}
    >
      <Flex
        align="center"
        p="3"
        role="group"
        cursor="pointer"
        _hover={{
          bg: useColorModeValue('brand.50', 'brand.900'),
          color: useColorModeValue('brand.600', 'brand.300'),
        }}
        borderRadius="md"
        {...rest}
      >
        {icon && (
          <Icon
            mr="4"
            fontSize="16"
            as={icon}
          />
        )}
        {children}
      </Flex>
    </NavLink>
  );
};

export default Sidebar;